import { Transaction } from 'olympe';
import { getLogger } from '@olympeio/core';
import React from 'react';

/**
 * DataGrid renderer type
 * @typedef {Object} DataGridRenderer
 * @property {string} name - The name of this renderer
 * @property {function(!Array<*>, !Object, !CloudObject, !BrickContext, !DataGridConfig):ReactElement} render
 *      - The render method with the following parameters:
 *          1. an array of the mapped properties as values
 *          2. an options object
 *          3. the current row instance
 *          4. the data grid execution context
 *          5. the current column config
 * @property {function(Object, CloudObject, BrickContext, Object, Property):ReactElement} renderForEdition
 *      - The renderForEdition method with the following parameters:
 *          1. the current value of the mapped property
 *          2. the current row instance
 *          3. the current property
 *          4. the datagrid execution context
 *          5. an options object
 *          6. the current column config
 * @property {function(!Array<*>, !Object, !CloudObject, !BrickContext, !DataGridConfig):boolean} isEditable
 *      - The isEditable method with the following parameters (column option "editable" must be set to true):
 *          1. an array of the mapped properties as values
 *          2. an options object
 *          3. the current row instance
 *          4. the data grid execution context
 *          5. the current column config
 */

/**
 * DataGrid registry type
 */
class DataGridRegistry {
    constructor() {
        /**
         * @type {!Map<string, !DataGridRenderer>}
         */
        this.renderers = new Map();
    }
}

/**
 * DataGrid registry singleton
 */
const dataGridRegistry = new DataGridRegistry();

/**
 * Register a new renderer in the DataGrid registry
 * @param {!DataGridRenderer} renderer
 */
export function registerDataGridRenderer(renderer) {
    dataGridRegistry.renderers.set(renderer.name, renderer);
}

/**
 * Get a renderer from the DataGrid registry
 * @param {string} name
 * @return {DataGridRenderer}
 */
export function getDataGridRenderer(name) {
    return dataGridRegistry.renderers.get(name) || dataGridRegistry.renderers.get('DEFAULT');
}

/**
 * DEFAULT
 * Default renderer
 */
registerDataGridRenderer({
    name: 'DEFAULT',
    render: () => {
        return <p>{'INVALID RENDERER'}</p>;
    },
    renderForEdition: () => {
        return <p>{'INVALID EDIT RENDERER'}</p>;
    },
});

/**
 * Helper to use in renderForEdition()
 * @param {!BrickContext} $
 * @param {!CloudObject} object
 * @param {!Property} property
 * @param {!*} value
 */
export function updateProperty($, object, property, value) {
    const logger = getLogger('Enhanced DataGrid Edition');
    const t = Transaction.from($);
    t.update(object, property, value);
    Transaction.process($, t)
        .then(() => {
            $.set('Edited Object', object);
            $.trigger('On Object Edited');
            logger.info(`value updated: ${value}`);
        })
        .catch((error) => logger.error(`value not updated: ${value} -> ${error}`));
}
